import styles from './DeliveryDeal.module.css';

import type { DeliveryPayMode } from 'src/components/DeliveryPayModal';

import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import classNames from 'classnames';
import { useParams, useNavigate } from 'react-router';
import { X, Truck, ArrowCounterClockwise } from '@phosphor-icons/react';
import React, { useMemo, useState, useEffect, useCallback } from 'react';

import { useDocumentTitle } from 'src/hooks/useDocumentTitle';
import { useQueryScreenState } from 'src/hooks/useQueryScreenState';

import { formatDay } from 'src/utils/date';
import { resolveDealChain } from 'src/utils/dealChain';
import { formatImageUrl } from 'src/utils/format-image';
import { noPhotoAlt, carPhotoAlt } from 'src/utils/imageAlt';
import { buildDeliveryTracker } from 'src/utils/dealTracker';
import { carTitle as carNameOf } from 'src/utils/documentTitle';
import { deliveryInvoiceView } from 'src/utils/deliveryInvoice';
import { getApiErrorMessage } from 'src/utils/listingAvailability';
import { ESCROW_NETWORK_FEES_NOTE } from 'src/utils/escrowCreation';
import {
  isDeliveryTerminal,
  disputeActionVisibility,
  deliveryDealFingerprint,
  deliveryActionVisibility,
} from 'src/utils/deliveryActions';

import { useWalletUI, WalletButton } from 'src/blockchain';
import currencyImage from 'src/assets/images/currency.png';
import { useBuyerChains, useDeliveryDeal, useConfirmDeliveryDeal, useWithdrawDeliveryDeal } from 'src/queries';

import { Modal } from 'src/components/Modal';
import { Loader } from 'src/components/Loader';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/themed/Stack';
import { Button } from 'src/components/themed/Button';
import { DealTracker } from 'src/components/DealTracker';
import { InvoiceCard } from 'src/components/InvoiceCard';
import { Typography } from 'src/components/themed/Typography';
import { RequestFailureState } from 'src/components/EmptyState';
import { ActionButton } from 'src/components/themed/ActionButton';
import { DeliveryPayModal } from 'src/components/DeliveryPayModal';
import { TxProgress, useTxProgress, useClearWhenWalletConnects } from 'src/components/TxProgress';

import { DeliveryVote } from 'src/types/dealStep';

import { DeliveryDealStatus } from '../../types/delivery';

/**
 * The buyer's delivery deal (contracts v3, UI part §2–§5).
 *
 * Three things this screen owes the buyer, and each is a component rather than a paragraph here:
 * the tracker says where the order is, the invoice says what it costs and proves what was paid,
 * and `TxProgress` narrates every signature from the wallet prompt to the updated status.
 *
 * The deal's chain is read off the deal — not off the build — and everything chain-shaped follows
 * from it: which wallet family signs, which stablecoin the amounts are in, and where a transaction
 * hash links. A screen that assumed one chain is exactly how a EUR deal on Base ends up labelled
 * in USDT and signed on whatever network the wallet happened to sit on.
 */

// "5m 12s" — coarse countdown to a deadline.
const formatRemaining = (seconds: number): string => {
  if (seconds <= 0) return '0s';
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return minutes > 0 ? `${minutes}m ${secs}s` : `${secs}s`;
};

const VOTE_LABEL: Record<DeliveryVote, string> = {
  [DeliveryVote.None]: 'has not signed yet',
  [DeliveryVote.Refund]: 'signed: refund to you',
  [DeliveryVote.Payout]: 'signed: pay the dealer',
};

type OnchainAction = 'cancel' | 'dispute' | 'signRefund' | 'signPayout' | 'claimStale';

const ACTION_CALL: Record<OnchainAction, { signature: string; args?: readonly (string | number | boolean)[] }> = {
  cancel: { signature: 'cancel()' },
  dispute: { signature: 'raiseDispute()' },
  signRefund: { signature: 'signResolution(bool)', args: [true] },
  signPayout: { signature: 'signResolution(bool)', args: [false] },
  claimStale: { signature: 'claimStaleDispute()' },
};

const ACTION_CONFIRM: Record<OnchainAction, { header: string; body: string; button: string; danger?: boolean }> = {
  cancel: {
    header: 'Cancel this order?',
    body: 'The car price and delivery are refunded to your wallet; the platform fee is non-refundable. This is an on-chain action.',
    button: 'Yes, cancel & refund',
    danger: true,
  },
  dispute: {
    header: 'Raise a dispute?',
    body: 'Pickup and cancellation are paused until two of the three parties — you, the dealer and GAC — sign the same outcome. This is an on-chain action.',
    button: 'Yes, raise dispute',
  },
  signRefund: {
    header: 'Sign a refund to yourself?',
    body: 'Your signature counts as one of the two needed. The refund executes the moment a second party signs the same outcome.',
    button: 'Sign refund',
  },
  signPayout: {
    header: 'Sign a payout to the dealer?',
    body: 'Your signature counts as one of the two needed. The payout executes the moment a second party signs the same outcome.',
    button: 'Sign payout',
  },
  claimStale: {
    header: 'Claim your refund?',
    body: 'The dispute has been open for 14 days without a resolution, so the contract lets you take the refund alone. This is an on-chain action.',
    button: 'Claim refund',
  },
};

export const DeliveryDeal = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [payMode, setPayMode] = useState<DeliveryPayMode | null>(null);
  // Set right after a successful fund() so "Pay now" doesn't reappear during the seconds before
  // the poller projects `escrowed` (a second fund would just revert and burn the network fee).
  const [fundSent, setFundSent] = useState(false);
  const [confirmAction, setConfirmAction] = useState<OnchainAction | null>(null);
  // Kept past the dialog so a failed signature can be offered again by name.
  const [lastAction, setLastAction] = useState<OnchainAction | null>(null);
  const [actionError, setActionError] = useState('');
  const [nowTick, setNowTick] = useState(() => Date.now());
  // What the deal looked like when a signature was sent. The progress panel stays up until the deal
  // actually differs — ending at "confirmed" would show success over a screen still showing the old
  // state, which is the moment a buyer presses the same button twice.
  const [signedFromStatus, setSignedFromStatus] = useState<string | null>(null);

  const dealQuery = useDeliveryDeal(id!);
  const { data: deal, refetch } = dealQuery;
  const screenState = useQueryScreenState(dealQuery);
  const { data: buyerChains } = useBuyerChains();
  useDocumentTitle(deal?.listing ? carNameOf(deal.listing) : undefined, 'Delivery order');

  const chain = useMemo(
    () =>
      deal
        ? resolveDealChain({
            chainKey: deal.chainKey,
            buyerChains,
            listingCurrency: deal.listing?.currency,
          })
        : null,
    [deal, buyerChains]
  );

  const walletUI = useWalletUI(chain?.family);
  const tx = useTxProgress();

  useClearWhenWalletConnects(tx, chain?.family);

  const withdraw = useWithdrawDeliveryDeal();
  const confirm = useConfirmDeliveryDeal();

  const status = deal?.status;
  const terminal = status ? isDeliveryTerminal(status) : false;

  // 1s ticker for the offer/payment countdown and for the stale-claim deadline.
  useEffect(() => {
    if (!deal || terminal) {
      return undefined;
    }
    const interval = setInterval(() => setNowTick(Date.now()), 1000);

    return () => clearInterval(interval);
  }, [deal, terminal]);

  const handleClose = useCallback(() => navigate('/deals', { replace: true }), [navigate]);

  const handleWithdraw = async () => {
    setActionError('');
    try {
      await withdraw.mutateAsync({ id: deal!.id });
    } catch (err) {
      setActionError(getApiErrorMessage(err, 'Could not withdraw the offer. Please try again.'));
    }
  };

  const handleConfirm = async () => {
    setActionError('');
    try {
      await confirm.mutateAsync({ id: deal!.id });
    } catch (err) {
      setActionError(getApiErrorMessage(err, 'Could not confirm. The payment window may have expired.'));
    }
  };

  /** Buyer-signed contract calls: cancel, dispute, the dispute signatures and the stale claim. */
  const handleOnchainAction = async () => {
    if (!deal?.contractAddress || !confirmAction) {
      return;
    }

    const call = ACTION_CALL[confirmAction];

    setActionError('');
    setConfirmAction(null);
    setLastAction(confirmAction);
    setSignedFromStatus(deliveryDealFingerprint(deal));

    await tx.run(
      (onProgress) =>
        walletUI.callContract({
          contractAddress: deal.contractAddress as string,
          signature: call.signature,
          args: call.args,
          chainId: chain?.chainId,
          onProgress,
        }),
      'Transaction failed. Please try again.'
    );

    refetch();
  };

  if (screenState === 'loading') {
    return (
      <div className={classNames('container', styles.container)}>
        <Loader />
      </div>
    );
  }

  if (screenState !== 'ready' || !deal) {
    // A 404 or a 500 used to leave a blank page (app#210).
    return (
      <div className={classNames('container', styles.container)}>
        <Stack width="100%" p={4} pb={20} maxWidth="750px">
          <RequestFailureState
            titleAs="h1"
            kind={screenState === 'ready' ? 'failed' : screenState}
            subject="This order"
            onBack={handleClose}
            backLabel="Back to deals"
            onRetry={() => refetch()}
          />
        </Stack>
      </div>
    );
  }

  const vis = deliveryActionVisibility(deal, { escrowCreation: chain?.escrowCreation });
  const dispute = disputeActionVisibility(deal, chain?.family, nowTick);
  const invoice = deliveryInvoiceView(deal, chain);
  const tracker = buildDeliveryTracker(deal, chain);

  // Countdown deadline by phase (pre-escrow only).
  const deadlineIso =
    deal.status === DeliveryDealStatus.Offered
      ? deal.offerExpiresAt
      : deal.status === DeliveryDealStatus.Approved || deal.status === DeliveryDealStatus.AwaitingPayment
        ? (deal.paymentExpiresAt ?? deal.offerExpiresAt)
        : null;
  const remainingSeconds = deadlineIso ? Math.floor((dayjs(deadlineIso).valueOf() - nowTick) / 1000) : 0;
  const showCountdown = !!deadlineIso && remainingSeconds > 0;

  // Empty while the listing is unknown, so the photo placeholder says "no photo yet" rather than
  // "no photo of Delivery order yet".
  const carName = deal.listing ? carNameOf(deal.listing) : '';
  const carTitle = carName || 'Delivery order';

  // The signature landed and the deal has moved on: the panel has said everything it can.
  //
  // "Moved on" is not the status alone. A first vote in a dispute changes nothing else — the deal
  // stays `disputed` and only the buyer's own vote flips — so a status-only comparison would leave
  // the panel spinning on "updating your deal" forever, over a deal that had already updated.
  const syncDone = tx.state?.phase === 'syncing' && deliveryDealFingerprint(deal) !== signedFromStatus;
  // Every on-chain button is disabled while one signature is in flight — the second click is the
  // one that costs a network fee for a transaction that reverts.
  const busy = tx.isRunning && !syncDone;
  // Nothing on-chain can be signed before the deal's network is known: the wallet would have to be
  // switched to a chain id nobody resolved, and this build may not even carry that family.
  const canSignOnchain = !!chain?.signable && !!deal.contractAddress;

  return (
    <div className={classNames('container', styles.container)}>
      <Stack width="100%" gap={2} p={4} pb={24} maxWidth="750px">
        <Stack width="100%" flexDirection="row" justifyContent="space-between" alignItems="center" pl={4}>
          <Typography as="h1" fontWeight="600" variant="title3" color="white">
            Delivery order
          </Typography>
          <Stack flexDirection="row" gap={2} alignItems="center">
            <ActionButton onClick={refetch} label="Refresh the order">
              <ArrowCounterClockwise size={22} color="white" />
            </ActionButton>
            <ActionButton onClick={handleClose} label="Back to deals">
              <X size={24} color="white" />
            </ActionButton>
          </Stack>
        </Stack>

        <Stack width="100%" gap={4}>
          <ListItem
            title={carTitle}
            description={deal.listing?.vin ?? `Order ${deal.orderRef}`}
            image={
              deal.listing && !isEmpty(deal.listing.images) ? (
                <img src={formatImageUrl(deal.listing.images[0])} alt={carPhotoAlt(carName)} className={styles.image} />
              ) : (
                <img src={currencyImage} alt={noPhotoAlt(carName)} className={classNames([styles.image, styles.defaultImage])} />
              )
            }
          />

          <DealTracker tracker={tracker} chain={chain} />

          {showCountdown ? (
            <Typography color="lightWhite" variant="small" textAlign="center">
              {deal.status === DeliveryDealStatus.Offered
                ? `Offer expires in ${formatRemaining(remainingSeconds)}`
                : `Payment window: ${formatRemaining(remainingSeconds)} left`}
            </Typography>
          ) : null}

          <InvoiceCard
            lines={invoice.lines}
            total={invoice.total}
            footnote={invoice.footnote}
            // The buyer's own wallet is about to create the contract and pay (api#380): what the
            // total does not include, said once, under it.
            note={vis.needsEscrow && chain?.family === 'evm' ? ESCROW_NETWORK_FEES_NOTE : null}
            issuedAt={invoice.issuedAt}
            paymentTxHash={invoice.paymentTxHash}
            chain={chain}
            meta={[
              { label: 'Order', value: deal.orderRef },
              { label: 'Recipient', value: deal.recipientFullName },
              { label: 'Phone', value: deal.recipientPhone },
              { label: 'City', value: deal.citySnapshot },
            ]}
          />

          {/* The logist the dealer chose, with the phone and the email (partners-spec §3.5) — api sends
              them from `escrowed`, when the money is on the contract, and nothing before. */}
          {deal.logist ? (
            <Stack gap={1} className={styles.section} data-testid="your-logist">
              <Typography color="white" fontWeight="600">
                Your logist
              </Typography>
              <Typography color="lightWhite" variant="small">
                {deal.logist.name}
              </Typography>
              {deal.logist.phone ? (
                <Typography color="lightWhite" variant="small">
                  Phone: {deal.logist.phone}
                </Typography>
              ) : null}
              <Typography color="lightWhite" variant="small">
                Email: {deal.logist.email}
              </Typography>
            </Stack>
          ) : null}

          {/* Handover code — buyer-only, shown from `escrowed` (spec §4.11). */}
          {vis.showHandoverCode && deal.handoverCode ? (
            <Stack gap={1} className={styles.codeBox}>
              <Typography color="lightWhite" variant="small">
                Your handover code
              </Typography>
              <Typography color="white" className={styles.code}>
                {deal.handoverCode}
              </Typography>
              <Typography color="lightWhite" variant="small">
                Show this code only when you receive the car.
              </Typography>
            </Stack>
          ) : null}

          {dispute.isDisputed && !dispute.supportsSignatures ? (
            // The pre-v3 clone on TRON has no signResolution at all: its only resolution path is
            // the platform's own `resolve(bool)`. Offering the buttons here would send a selector
            // the contract does not have — a revert the buyer pays for (app#192).
            <Stack gap={1} className={styles.section}>
              <Typography color="white" fontWeight="600">
                Dispute — GAC settles it
              </Typography>
              <Typography color="lightWhite" variant="small">
                Your money stays in escrow until GAC resolves this dispute. This escrow generation has no
                two-of-three signatures — GAC decides the refund or the payout.
              </Typography>
            </Stack>
          ) : null}

          {dispute.isDisputed && dispute.supportsSignatures ? (
            <Stack gap={1} className={styles.section}>
              <Typography color="white" fontWeight="600">
                Dispute — two signatures of three decide it
              </Typography>
              <Typography color="lightWhite" variant="small">
                You {VOTE_LABEL[deal.buyerVote]}
              </Typography>
              <Typography color="lightWhite" variant="small">
                The dealer {VOTE_LABEL[deal.dealerVote]}
              </Typography>
              <Typography color="lightWhite" variant="small">
                GAC {VOTE_LABEL[deal.platformVote]}
              </Typography>
              {dispute.staleClaimAvailableAt && !dispute.canClaimStale ? (
                <Typography color="lightWhite" variant="small">
                  If nobody resolves it, you can claim the refund alone from{' '}
                  {formatDay(dispute.staleClaimAvailableAt)}.
                </Typography>
              ) : null}
            </Stack>
          ) : null}

          <TxProgress
            state={syncDone ? null : tx.state}
            error={tx.error}
            // No wallet to sign with: `Connect Wallet` and the connection window stand in for Retry (app#315).
            connect={tx.needsWallet && chain ? <WalletButton family={chain.family} /> : null}
            explorerTxUrl={chain?.explorerTxUrl}
            // A real retry: the same action, asked again — not a button that only clears the box.
            onRetry={
              lastAction
                ? () => {
                    tx.reset();
                    setConfirmAction(lastAction);
                  }
                : undefined
            }
          />

          {actionError ? (
            <Typography color="white" variant="small" className={styles.error}>
              {actionError}
            </Typography>
          ) : null}

          {/* Actions by status (visibility mirrors the api rules + contract guards). */}
          {/* `canConfirm` and `isDeploying` overlap on awaiting_payment-without-contract;
              the deploying block below owns the retry there, so gate the main CTA off it. */}
          {vis.canConfirm && !vis.isDeploying ? (
            <Button
              onClick={handleConfirm}
              title="Confirm & pay"
              variant="accent"
              loading={confirm.isPending}
              className={styles.actionButton}
            />
          ) : null}

          {vis.isDeploying ? (
            <>
              <Button
                onClick={() => {}}
                title="Creating the deal contract…"
                variant="extraLightWhite"
                disabled
                className={styles.actionButton}
              />
              <Button
                onClick={handleConfirm}
                title="Retry"
                variant="extraLightWhite"
                loading={confirm.isPending}
                className={styles.actionButton}
              />
            </>
          ) : null}

          {vis.canPay ? (
            fundSent ? (
              <Button
                onClick={() => {}}
                title="Waiting for on-chain confirmation…"
                variant="extraLightWhite"
                disabled
                className={styles.actionButton}
              />
            ) : (
              <Button
                onClick={() => setPayMode('fund')}
                title="Pay now"
                variant="accent"
                disabled={busy}
                className={styles.actionButton}
              />
            )
          ) : null}

          {vis.canPayGas ? (
            <Button
              onClick={() => setPayMode('gas')}
              title="Pay gas fee"
              variant="extraLightWhite"
              disabled={busy}
              className={styles.actionButton}
            />
          ) : null}

          {vis.canCancel && canSignOnchain ? (
            <Button
              onClick={() => setConfirmAction('cancel')}
              title="Cancel order"
              variant="danger"
              disabled={busy}
              className={styles.actionButton}
            />
          ) : null}

          {vis.canRaiseDispute && canSignOnchain ? (
            <Button
              onClick={() => setConfirmAction('dispute')}
              title="Raise a dispute"
              variant="extraLightWhite"
              disabled={busy}
              className={styles.actionButton}
            />
          ) : null}

          {dispute.canSign && canSignOnchain ? (
            <>
              <Button
                onClick={() => setConfirmAction('signRefund')}
                title="Sign refund to me"
                variant="accent"
                disabled={busy}
                className={styles.actionButton}
              />
              <Button
                onClick={() => setConfirmAction('signPayout')}
                title="Sign payout to the dealer"
                variant="extraLightWhite"
                disabled={busy}
                className={styles.actionButton}
              />
            </>
          ) : null}

          {dispute.canClaimStale && canSignOnchain ? (
            <Button
              onClick={() => setConfirmAction('claimStale')}
              title="Claim refund"
              variant="accent"
              disabled={busy}
              className={styles.actionButton}
            />
          ) : null}

          {vis.canWithdraw ? (
            <Button
              onClick={handleWithdraw}
              title="Withdraw offer"
              variant="extraLightWhite"
              loading={withdraw.isPending}
              className={styles.actionButton}
            />
          ) : null}
        </Stack>
      </Stack>

      {payMode ? (
        <DeliveryPayModal
          visible={payMode !== null}
          onClose={() => setPayMode(null)}
          deal={deal}
          chain={chain}
          mode={payMode}
          onPaid={() => {
            // A funded escrow must not offer "Pay now" again until the poller advances.
            if (payMode === 'fund') {
              setFundSent(true);
            }
            refetch();
          }}
        />
      ) : null}

      {confirmAction ? (
        <Modal
          visible={confirmAction !== null}
          onClose={() => setConfirmAction(null)}
          icon={<Truck size={64} color="white" weight="fill" />}
          header={ACTION_CONFIRM[confirmAction].header}
          buttonText={ACTION_CONFIRM[confirmAction].button}
          buttonVariant={ACTION_CONFIRM[confirmAction].danger ? 'danger' : 'accent'}
          onSubmit={handleOnchainAction}
        >
          <Typography color="lightWhite" variant="small" textAlign="center">
            {ACTION_CONFIRM[confirmAction].body}
          </Typography>
          <Button title="Not now" onClick={() => setConfirmAction(null)} />
        </Modal>
      ) : null}
    </div>
  );
};
